
import React, { useRef, useEffect, useState, useMemo } from 'react';

interface BreathingCanvasProps {
  breathingPhase: 'inhale' | 'hold' | 'exhale';
  secondsLeft: number;
  cyclesCompleted: number;
  isMobile: boolean;
}

const BreathingCanvas: React.FC<BreathingCanvasProps> = ({ 
  breathingPhase, 
  secondsLeft, 
  cyclesCompleted,
  isMobile
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestIdRef = useRef<number | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Use memo for values that only change when dependencies change
  const canvasSize = useMemo(() => isMobile ? 280 : 450, [isMobile]);
  
  const circleColors = useMemo(() => ({
    inhale: 'rgba(66, 153, 225, 0.6)', // Blue for inhale
    hold: 'rgba(154, 230, 180, 0.6)', // Green for hold
    exhale: 'rgba(226, 232, 240, 0.6)' // Gray for exhale
  }), []);
  
  const fontSizes = useMemo(() => ({
    large: isMobile ? 18 : 24,
    small: isMobile ? 14 : 18
  }), [isMobile]);
  
  // Set up canvas dimensions
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    setDimensions({ width: canvasSize, height: canvasSize });
    
    // Cancel any previous animation frame
    if (requestIdRef.current !== null) {
      cancelAnimationFrame(requestIdRef.current);
    }
    
    // Render the initial frame
    renderCanvas();
    
    return () => {
      if (requestIdRef.current !== null) {
        cancelAnimationFrame(requestIdRef.current);
      }
    };
  }, [canvasSize]);
  
  // Render canvas when state changes
  const renderCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);
    
    // Center coordinates
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    
    // Circle radiuses
    const minRadius = isMobile ? 35 : 50;
    const maxRadius = isMobile ? 120 : 200;
    let currentRadius = breathingPhase === 'inhale' ? minRadius + (maxRadius - minRadius) * (1 - secondsLeft / 4) : 
                        breathingPhase === 'exhale' ? maxRadius - (maxRadius - minRadius) * (1 - secondsLeft / 4) : 
                        maxRadius;
    
    // Draw background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, maxRadius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(200, 200, 255, 0.2)';
    ctx.fill();
    
    // Draw breathing circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, currentRadius, 0, Math.PI * 2);
    
    // Different colors for different phases
    ctx.fillStyle = circleColors[breathingPhase];
    ctx.fill();
    
    // Draw text in middle
    ctx.fillStyle = '#000';
    ctx.font = `${fontSizes.large}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${breathingPhase}`, centerX, centerY - (isMobile ? 10 : 15));
    ctx.fillText(`${secondsLeft}`, centerX, centerY + (isMobile ? 10 : 15));
    
    // Draw progress
    ctx.fillStyle = '#000';
    ctx.font = `${fontSizes.small}px Arial`;
    ctx.fillText(`Cycles: ${cyclesCompleted}`, centerX, dimensions.height - (isMobile ? 20 : 30));
  };
  
  // Use effect for animation frame updates
  useEffect(() => {
    // Only re-render when these values change
    if (requestIdRef.current !== null) {
      cancelAnimationFrame(requestIdRef.current);
    }
    
    requestIdRef.current = requestAnimationFrame(renderCanvas);
    return () => {
      if (requestIdRef.current !== null) {
        cancelAnimationFrame(requestIdRef.current);
      }
    };
  }, [breathingPhase, secondsLeft, cyclesCompleted, dimensions.width, dimensions.height]);

  return (
    <canvas 
      ref={canvasRef} 
      className="mx-auto block touch-none"
      style={{ maxWidth: '100%', height: 'auto' }}
      role="img"
      aria-label={`Breathing visualization: ${breathingPhase} phase, ${secondsLeft} seconds left, ${cyclesCompleted} cycles completed`}
    />
  );
};

export default React.memo(BreathingCanvas);
